/**
 * Company Settings
 * Manage company preferences, accountant access, and network settings
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import {
  Users,
  UserPlus,
  Settings,
  Shield,
  Network,
  Eye,
  Edit,
  Lock,
  Trash2,
  Search,
  Building2,
  CheckCircle2,
  Info,
  BarChart3,
  FileText,
  Download,
  ExternalLink,
  CreditCard,
  Zap,
  Crown,
  Sparkles,
  Receipt,
} from "lucide-react";
import { Link } from "wouter";

const ACCESS_LEVELS = [
  {
    value: "read_only",
    label: "Read Only",
    icon: Eye,
    description: "View transactions and reports",
  },
  {
    value: "categorize",
    label: "Categorize",
    icon: Edit,
    description: "View + modify categories",
  },
  {
    value: "full_access",
    label: "Full Access",
    icon: Lock,
    description: "All except delete company",
  },
];

export default function CompanySettings() {
  return (
    <DashboardLayout>
      <SettingsContent />
    </DashboardLayout>
  );
}

const VALID_TABS = ["accountants", "network", "documents", "billing"] as const;
type SettingsTab = (typeof VALID_TABS)[number];

function getInitialTab(): SettingsTab {
  const hash = window.location.hash.slice(1) as SettingsTab;
  return VALID_TABS.includes(hash) ? hash : "accountants";
}

function SettingsContent() {
  const { selectedCompanyId } = useCompany();
  const [activeTab, setActiveTab] = useState<SettingsTab>(getInitialTab);

  const handleTabChange = (value: string) => {
    const tab = value as SettingsTab;
    setActiveTab(tab);
    window.location.hash = tab;
  };

  if (!selectedCompanyId) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold">No company selected</h2>
        <p className="text-muted-foreground">
          Select a company to manage settings.
        </p>
      </div>
    );
  }

  const companyId = selectedCompanyId;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
        <p className="text-muted-foreground">
          Manage your company's preferences and access control
        </p>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList className="overflow-x-auto flex-nowrap w-full justify-start">
          <TabsTrigger value="accountants">
            <Users className="mr-2 h-4 w-4" />
            Accountants
          </TabsTrigger>
          <TabsTrigger value="network">
            <Network className="mr-2 h-4 w-4" />
            Network Learning
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="mr-2 h-4 w-4" />
            Source Documents
          </TabsTrigger>
          <TabsTrigger value="billing">
            <CreditCard className="mr-2 h-4 w-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        {/* Accountants Tab */}
        <TabsContent value="accountants" className="space-y-4">
          <AccountantsSection companyId={companyId} />
        </TabsContent>

        {/* Network Learning Tab */}
        <TabsContent value="network" className="space-y-4">
          <NetworkSettingsSection companyId={companyId} />
        </TabsContent>

        {/* Source Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <SourceDocumentsSection companyId={companyId} />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-4">
          <BillingSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccountantsSection({ companyId }: { companyId: number }) {
  const utils = trpc.useUtils();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedAccountant, setSelectedAccountant] = useState<{
    id: number;
    firmName: string;
    relationshipId: number;
  } | null>(null);

  // Get accountants for this company
  const { data: accountantsData, isLoading } =
    trpc.accountant.listForCompany.useQuery(
      { companyId },
      { enabled: !!companyId }
    );

  // Revoke access mutation
  const revokeMutation = trpc.accountant.revokeAccess.useMutation({
    onSuccess: () => {
      utils.accountant.listForCompany.invalidate({ companyId });
      setRevokeDialogOpen(false);
      setSelectedAccountant(null);
    },
  });

  // Update access level mutation
  const updateAccessMutation = trpc.accountant.updateAccessLevel.useMutation({
    onSuccess: () => {
      utils.accountant.listForCompany.invalidate({ companyId });
    },
  });

  const handleRevokeClick = (accountant: {
    id: number;
    firmName: string;
    relationshipId: number;
  }) => {
    setSelectedAccountant(accountant);
    setRevokeDialogOpen(true);
  };

  const activeAccountants =
    accountantsData?.filter(a => a.status === "active") || [];
  const pendingAccountants =
    accountantsData?.filter(a => a.status === "pending") || [];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Accountant Access</CardTitle>
              <CardDescription>
                Manage who has access to your company's financial data
              </CardDescription>
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite Accountant
                </Button>
              </DialogTrigger>
              <InviteAccountantDialog
                companyId={companyId}
                onSuccess={() => {
                  setInviteDialogOpen(false);
                  utils.accountant.listForCompany.invalidate({ companyId });
                }}
              />
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">
                Loading...
              </div>
            </div>
          ) : activeAccountants.length > 0 || pendingAccountants.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Accountant</TableHead>
                  <TableHead>Access Level</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Since</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...activeAccountants, ...pendingAccountants].map(
                  accountant => {
                    const accessConfig = ACCESS_LEVELS.find(
                      l => l.value === accountant.accessLevel
                    );
                    const AccessIcon = accessConfig?.icon || Eye;

                    return (
                      <TableRow key={accountant.relationshipId}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium">
                                {accountant.firmName}
                              </div>
                              {accountant.isVerified && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <CheckCircle2 className="h-3 w-3 text-[var(--success)]" />
                                  Verified
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {accountant.status === "active" ? (
                            <Select
                              value={accountant.accessLevel}
                              onValueChange={value => {
                                updateAccessMutation.mutate({
                                  companyId,
                                  accountantId: accountant.accountantId,
                                  accessLevel: value as
                                    | "read_only"
                                    | "categorize"
                                    | "full_access",
                                });
                              }}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ACCESS_LEVELS.map(level => (
                                  <SelectItem
                                    key={level.value}
                                    value={level.value}
                                  >
                                    <div className="flex items-center gap-2">
                                      <level.icon className="h-3 w-3" />
                                      {level.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex items-center gap-2">
                              <AccessIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="capitalize">
                                {accountant.accessLevel.replace("_", " ")}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              accountant.status === "active"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {accountant.status === "active"
                              ? "Active"
                              : "Pending"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {accountant.acceptedAt
                            ? new Date(
                                accountant.acceptedAt
                              ).toLocaleDateString()
                            : new Date(
                                accountant.invitedAt
                              ).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRevokeClick({
                                id: accountant.accountantId,
                                firmName: accountant.firmName,
                                relationshipId: accountant.relationshipId,
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No accountants yet</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                Invite an accountant to help manage your company's finances and
                tax compliance.
              </p>
              <Link href="/accountants">
                <Button variant="outline">
                  <Search className="mr-2 h-4 w-4" />
                  Browse Marketplace
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoke Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {selectedAccountant?.firmName}'s access to your
              company. They will no longer be able to view or manage your
              financial data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedAccountant) {
                  revokeMutation.mutate({
                    companyId,
                    accountantId: selectedAccountant.id,
                  });
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke Access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function InviteAccountantDialog({
  companyId,
  onSuccess,
}: {
  companyId: number;
  onSuccess: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAccountant, setSelectedAccountant] = useState<number | null>(
    null
  );
  const [accessLevel, setAccessLevel] = useState<
    "read_only" | "categorize" | "full_access"
  >("read_only");

  // Search accountants
  const { data: searchResults, isLoading: isSearching } =
    trpc.accountant.search.useQuery(
      { query: searchQuery, acceptingClientsOnly: true, limit: 10 },
      { enabled: searchQuery.length >= 2 }
    );

  // Invite mutation
  const inviteMutation = trpc.accountant.inviteToCompany.useMutation({
    onSuccess: () => {
      onSuccess();
      setSearchQuery("");
      setSelectedAccountant(null);
    },
  });

  const handleInvite = () => {
    if (selectedAccountant) {
      inviteMutation.mutate({
        companyId,
        accountantId: selectedAccountant,
        accessLevel,
      });
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Invite Accountant</DialogTitle>
        <DialogDescription>
          Search for an accountant and grant them access to your company
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        {/* Search */}
        <div className="space-y-2">
          <Label>Search Accountants</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by firm name..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setSelectedAccountant(null);
              }}
              className="pl-10"
            />
          </div>
        </div>

        {/* Search Results */}
        {searchQuery.length >= 2 && (
          <div className="border rounded-lg max-h-48 overflow-y-auto">
            {isSearching ? (
              <div className="p-4 text-center text-muted-foreground">
                Searching...
              </div>
            ) : searchResults?.accountants &&
              searchResults.accountants.length > 0 ? (
              searchResults.accountants.map(accountant => (
                <div
                  key={accountant.id}
                  className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted ${
                    selectedAccountant === accountant.id ? "bg-muted" : ""
                  }`}
                  onClick={() => setSelectedAccountant(accountant.id)}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{accountant.firmName}</span>
                      {accountant.isVerified && (
                        <CheckCircle2 className="h-4 w-4 text-[var(--success)]" />
                      )}
                    </div>
                    {accountant.cantons && accountant.cantons.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {accountant.cantons.slice(0, 3).join(", ")}
                      </span>
                    )}
                  </div>
                  {selectedAccountant === accountant.id && (
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-muted-foreground">
                No accountants found
              </div>
            )}
          </div>
        )}

        {/* Access Level */}
        {selectedAccountant && (
          <div className="space-y-2">
            <Label>Access Level</Label>
            <Select
              value={accessLevel}
              onValueChange={v => setAccessLevel(v as typeof accessLevel)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_LEVELS.map(level => (
                  <SelectItem key={level.value} value={level.value}>
                    <div className="flex items-center gap-2">
                      <level.icon className="h-4 w-4" />
                      <div>
                        <div className="font-medium">{level.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {level.description}
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          onClick={handleInvite}
          disabled={!selectedAccountant || inviteMutation.isPending}
        >
          {inviteMutation.isPending ? "Sending..." : "Send Invitation"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function NetworkSettingsSection({ companyId }: { companyId: number }) {
  const utils = trpc.useUtils();

  // Get network settings
  const { data: settings, isLoading } =
    trpc.networkLearning.getSettings.useQuery(
      { companyId },
      { enabled: !!companyId }
    );

  // Get network stats
  const { data: stats } = trpc.networkLearning.getStats.useQuery();

  // Update settings mutation
  const updateMutation = trpc.networkLearning.updateSettings.useMutation({
    onSuccess: () => {
      utils.networkLearning.getSettings.invalidate({ companyId });
    },
  });

  const handleToggleContribute = (checked: boolean) => {
    updateMutation.mutate({
      companyId,
      contributePatterns: checked,
    });
  };

  const handleToggleUse = (checked: boolean) => {
    updateMutation.mutate({
      companyId,
      useNetworkPatterns: checked,
    });
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Network Stats */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Network Patterns
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.publishedPatterns}
              </div>
              <p className="text-xs text-muted-foreground">
                of {stats.totalPatterns} total
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Contributors
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.totalContributors}
              </div>
              <p className="text-xs text-muted-foreground">
                anonymous companies
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Avg Confidence
              </CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.avgConfidence}%</div>
              <p className="text-xs text-muted-foreground">pattern accuracy</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Categories</CardTitle>
              <Network className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Object.keys(stats.patternsByType || {}).length}
              </div>
              <p className="text-xs text-muted-foreground">pattern types</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Network Learning Settings</CardTitle>
          <CardDescription>
            Control how your company participates in the anonymous pattern
            sharing network
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Use Network Patterns */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="use-network" className="text-base">
                Use Network Suggestions
              </Label>
              <p className="text-sm text-muted-foreground">
                Get categorization suggestions based on patterns from other
                companies
              </p>
            </div>
            <Switch
              id="use-network"
              checked={settings?.useNetworkPatterns ?? true}
              onCheckedChange={handleToggleUse}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="border-t" />

          {/* Contribute Patterns */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="contribute" className="text-base">
                Contribute to Network
              </Label>
              <p className="text-sm text-muted-foreground">
                Anonymously share your categorization patterns to help other
                companies
              </p>
            </div>
            <Switch
              id="contribute"
              checked={settings?.contributePatterns ?? false}
              onCheckedChange={handleToggleContribute}
              disabled={updateMutation.isPending}
            />
          </div>

          {/* Privacy Notice */}
          <div className="bg-muted/50 rounded-lg p-4 flex gap-3">
            <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                Privacy Protected
              </p>
              <p>
                All contributed patterns are anonymized using cryptographic
                hashing. Your company's identity, specific transactions, and
                amounts are never shared. Only generalized patterns (e.g.,
                "keyword X → category Y") are aggregated across 5+ companies
                before being made available to the network.
              </p>
            </div>
          </div>

          {settings?.consentedAt && (
            <p className="text-xs text-muted-foreground">
              Network contribution enabled on{" "}
              {new Date(settings.consentedAt).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Billing Section (gt-prd08)
// Stripe subscription billing + AI credit packs
// ============================================================================

const PLAN_TIERS = [
  {
    key: "free" as const,
    label: "Free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: ["1 company", "100 transactions/mo", "10 AI credits"],
    icon: Zap,
  },
  {
    key: "starter" as const,
    label: "Starter",
    monthlyPrice: 2900,
    yearlyPrice: 29000,
    monthlyPriceId: "price_1Sxtw9JY7GKchZDVceK0kikw",
    yearlyPriceId: "price_1SxtwAJY7GKchZDVqJYQuLKT",
    features: ["3 companies", "1,000 transactions/mo", "100 AI credits"],
    icon: Sparkles,
  },
  {
    key: "professional" as const,
    label: "Professional",
    monthlyPrice: 7900,
    yearlyPrice: 79000,
    monthlyPriceId: "price_1SxtwBJY7GKchZDVA3fmwXaL",
    yearlyPriceId: "price_1SxtwDJY7GKchZDV1ArVVLED",
    features: ["10 companies", "10,000 transactions/mo", "500 AI credits"],
    icon: Crown,
  },
  {
    key: "enterprise" as const,
    label: "Enterprise",
    monthlyPrice: 19900,
    yearlyPrice: 199000,
    monthlyPriceId: "price_1SxtwEJY7GKchZDVVhmsWd0x",
    yearlyPriceId: "price_1SxtwGJY7GKchZDV98pSmmTH",
    features: [
      "Unlimited companies",
      "Unlimited transactions",
      "Unlimited AI credits",
    ],
    icon: Shield,
  },
];

const AI_CREDIT_PACK_OPTIONS = [
  {
    label: "Small",
    credits: 100,
    price: 900,
    priceId: "price_1SxtwNJY7GKchZDVj4SrptQF",
  },
  {
    label: "Medium",
    credits: 500,
    price: 3900,
    priceId: "price_1SxtwPJY7GKchZDVJLf7Ldp3",
  },
  {
    label: "Large",
    credits: 2000,
    price: 12900,
    priceId: "price_1SxtwRJY7GKchZDV0TalwTlG",
  },
];

function formatCHF(cents: number): string {
  const chf = cents / 100;
  return `CHF ${chf.toLocaleString("de-CH", { minimumFractionDigits: chf % 1 === 0 ? 0 : 2 })}`;
}

function BillingSection() {
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">(
    "monthly"
  );

  // Queries
  const { data: currentPlan, isLoading: planLoading } =
    trpc.billing.getCurrentPlan.useQuery();
  const { data: creditBalance } = trpc.billing.getAiCreditBalance.useQuery();
  const { data: billingHistory } = trpc.billing.getBillingHistory.useQuery();

  // Mutations
  const checkoutMutation = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: data => {
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      }
    },
  });

  const portalMutation = trpc.billing.createPortalSession.useMutation({
    onSuccess: data => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });

  const creditsMutation = trpc.billing.purchaseAiCredits.useMutation({
    onSuccess: data => {
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      }
    },
  });

  const currentTier = currentPlan?.tier ?? "free";
  const baseUrl = window.location.origin;

  const handleUpgrade = (priceId: string) => {
    checkoutMutation.mutate({
      priceId,
      successUrl: `${baseUrl}/settings?billing=success`,
      cancelUrl: `${baseUrl}/settings?billing=canceled`,
    });
  };

  const handleManageBilling = () => {
    portalMutation.mutate({
      returnUrl: `${baseUrl}/settings`,
    });
  };

  const handleBuyCredits = (priceId: string) => {
    creditsMutation.mutate({
      priceId,
      successUrl: `${baseUrl}/settings?credits=success`,
      cancelUrl: `${baseUrl}/settings?credits=canceled`,
    });
  };

  if (planLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Current Plan
              </CardTitle>
              <CardDescription>
                Your active subscription and billing details
              </CardDescription>
            </div>
            {currentTier !== "free" && (
              <Button
                variant="outline"
                onClick={handleManageBilling}
                disabled={portalMutation.isPending}
              >
                <Settings className="mr-2 h-4 w-4" />
                {portalMutation.isPending ? "Opening..." : "Manage Billing"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Badge
              className="text-base px-3 py-1"
              variant={currentTier === "free" ? "secondary" : "default"}
            >
              {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
            </Badge>
            {currentPlan?.billingInterval && (
              <span className="text-sm text-muted-foreground">
                {currentPlan.billingInterval === "yearly"
                  ? "Annual"
                  : "Monthly"}{" "}
                billing
              </span>
            )}
            {currentPlan?.cancelAtPeriodEnd && (
              <Badge variant="destructive">Cancels at period end</Badge>
            )}
          </div>
          {currentPlan?.currentPeriodEnd && (
            <p className="text-sm text-muted-foreground mt-2">
              Current period ends:{" "}
              {new Date(currentPlan.currentPeriodEnd).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Plan Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Plans</CardTitle>
          <CardDescription>
            Choose the plan that fits your business
          </CardDescription>
          <div className="flex gap-2 mt-2">
            <Button
              variant={billingInterval === "monthly" ? "default" : "outline"}
              size="sm"
              onClick={() => setBillingInterval("monthly")}
            >
              Monthly
            </Button>
            <Button
              variant={billingInterval === "yearly" ? "default" : "outline"}
              size="sm"
              onClick={() => setBillingInterval("yearly")}
            >
              Yearly
              <Badge variant="secondary" className="ml-2 text-xs">
                Save 2 months
              </Badge>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {PLAN_TIERS.map(plan => {
              const isCurrentPlan = plan.key === currentTier;
              const price =
                billingInterval === "monthly"
                  ? plan.monthlyPrice
                  : plan.yearlyPrice;
              const priceId =
                billingInterval === "monthly"
                  ? (plan as Record<string, unknown>).monthlyPriceId
                  : (plan as Record<string, unknown>).yearlyPriceId;
              const PlanIcon = plan.icon;

              return (
                <Card
                  key={plan.key}
                  className={
                    isCurrentPlan ? "border-[var(--primary)] border-2" : ""
                  }
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <PlanIcon className="h-5 w-5 text-[var(--primary)]" />
                      <CardTitle className="text-lg">{plan.label}</CardTitle>
                    </div>
                    <div className="text-2xl font-bold">
                      {price === 0 ? (
                        "Free"
                      ) : (
                        <>
                          {formatCHF(price)}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{billingInterval === "monthly" ? "mo" : "yr"}
                          </span>
                        </>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <ul className="space-y-2 text-sm">
                      {plan.features.map(feature => (
                        <li key={feature} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-[var(--success)]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {isCurrentPlan ? (
                      <Button variant="outline" className="w-full" disabled>
                        Current Plan
                      </Button>
                    ) : plan.key === "free" ? (
                      <Button variant="outline" className="w-full" disabled>
                        Default
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        onClick={() => handleUpgrade(priceId as string)}
                        disabled={checkoutMutation.isPending}
                      >
                        {checkoutMutation.isPending
                          ? "Redirecting..."
                          : currentTier === "free"
                            ? "Upgrade"
                            : "Switch Plan"}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* AI Credits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Credits
          </CardTitle>
          <CardDescription>
            Purchase additional AI credits for document processing and analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-3xl font-bold">
                {creditBalance?.balance ?? 0}
              </p>
              <p className="text-sm text-muted-foreground">Credits remaining</p>
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Total purchased: {creditBalance?.totalPurchased ?? 0}</p>
              <p>Total used: {creditBalance?.totalUsed ?? 0}</p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {AI_CREDIT_PACK_OPTIONS.map(pack => (
              <Card key={pack.priceId}>
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <p className="font-semibold text-lg">{pack.label}</p>
                  <p className="text-2xl font-bold text-[var(--primary)]">
                    {pack.credits}
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">credits</p>
                  <p className="text-sm font-medium mb-3">
                    {formatCHF(pack.price)}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleBuyCredits(pack.priceId)}
                    disabled={creditsMutation.isPending}
                  >
                    {creditsMutation.isPending ? "..." : "Buy"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      {billingHistory && billingHistory.invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Billing History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billingHistory.invoices.map(invoice => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      {invoice.number ?? invoice.id.slice(0, 16)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(invoice.created).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{formatCHF(invoice.amountPaid)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          invoice.status === "paid"
                            ? "default"
                            : invoice.status === "open"
                              ? "secondary"
                              : "destructive"
                        }
                      >
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {invoice.hostedInvoiceUrl && (
                        <Button variant="ghost" size="sm" asChild>
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      {invoice.invoicePdf && (
                        <Button variant="ghost" size="sm" asChild>
                          <a
                            href={invoice.invoicePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Source Documents Section (gt-prd11)
// Shows documents that were uploaded during company discovery
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function getDocumentTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    bank_statement: "Bank Statement",
    tax_filing: "Tax Filing",
    financial_statement: "Financial Statement",
    vat_form: "VAT Form",
    invoice: "Invoice",
    receipt: "Receipt",
    contract: "Contract",
    correspondence: "Correspondence",
    pdf: "PDF Document",
    unknown: "Document",
  };
  return labels[type] || type;
}

function SourceDocumentsSection({ companyId }: { companyId: number }) {
  const { data, isLoading } =
    trpc.documentManagement.getSourceDocuments.useQuery(
      { companyId },
      { enabled: !!companyId }
    );

  const documents = data?.documents || [];
  const registryDocs = data?.registryDocuments || [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Source Documents
              </CardTitle>
              <CardDescription>
                Documents uploaded during company discovery that were used to
                extract the company profile, bank accounts, vendors, and
                customers.
              </CardDescription>
            </div>
            {documents.length > 0 && (
              <Badge variant="secondary">
                {documents.length} document{documents.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">
                Loading documents...
              </div>
            </div>
          ) : documents.length === 0 && registryDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No source documents found for this company.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Documents will appear here after completing company discovery.
              </p>
              <Link href="/company-discovery">
                <Button variant="outline" size="sm" className="mt-4">
                  <ExternalLink className="mr-2 h-3.5 w-3.5" />
                  Go to Company Discovery
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {doc.originalName || doc.fileName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getDocumentTypeLabel(doc.documentType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatFileSize(doc.fileSize)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {doc.uploadedAt
                        ? new Date(doc.uploadedAt).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          doc.status === "processed"
                            ? "default"
                            : doc.status === "error"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {doc.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {doc.fileUrl && (
                        <Button variant="ghost" size="sm" asChild>
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Document Registry Info (for power users) */}
      {registryDocs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              Document Registry
            </CardTitle>
            <CardDescription>
              Cross-import document intelligence records. These track document
              processing across different import paths.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {registryDocs.map(reg => (
                <div
                  key={reg.id}
                  className="flex items-center justify-between rounded-md border p-3"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{reg.fileName}</p>
                      <p className="text-xs text-muted-foreground">
                        {reg.classification
                          ? getDocumentTypeLabel(reg.classification)
                          : "Unknown type"}{" "}
                        {reg.classificationConfidence
                          ? `(${reg.classificationConfidence}% confidence)`
                          : ""}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>Import: {reg.importPath}</p>
                    <p>
                      Last processed:{" "}
                      {new Date(
                        Number(reg.lastProcessedAt)
                      ).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
